import "bootstrap/dist/css/bootstrap.min.css";
import { get, ref, update, onValue } from "firebase/database";
import { useState, useEffect } from "react";
import { db } from "../Firebase";
import { validatePhone, validateAadhaar } from "../utils/fieldValidation";
import { maskPhoneNumber, maskAadhaarNumber } from "../utils/maskingUtils";
import MaskedInput from "../components/MaskedInput";
import MaskedDisplay from "../components/MaskedDisplay";

const Search = () => {
  const [regId, setRegId] = useState("");
  const [basic, setBasic] = useState(null);
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState([]);
  const [passwordInput, setPasswordInput] = useState("");

  // Member view modal state
  const [showMemberView, setShowMemberView] = useState(false);
  const [memberViewData, setMemberViewData] = useState(null);
  const [showAllMembers, setShowAllMembers] = useState(false);

  // Member edit modal state
  const [showMemberEdit, setShowMemberEdit] = useState(false);
  const [memberEditData, setMemberEditData] = useState(null);
  const [currentRegId, setCurrentRegId] = useState("");

  // Master data states
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [urbanAreas, setUrbanAreas] = useState([]);

  useEffect(() => {
    // Fetch master data
    const fetchData = (path, setter) =>
      onValue(ref(db, path), snap =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : []
        )
      );

    fetchData("masters/states", setStates);
    fetchData("masters/cities", setCities);
    fetchData("masters/blocks", setBlocks);
    fetchData("masters/urbanAreas", setUrbanAreas);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!regId) {
      alert("कृपया परिवार ID / Aadhaar / Phone दर्ज करें");
      return;
    }

    if (!passwordInput) {
      alert('कृपया पासवर्ड दर्ज करें');
      return;
    }

    try {
      // try direct registration id
      const snapshot = await get(ref(db, `registration/${regId}`));

      if (snapshot.exists()) {
        const data = snapshot.val();
        const storedPwd = data.meta?.password;
        if (!storedPwd) { alert('पासवर्ड सेट नहीं है -- कृपया एडमिन से संपर्क करें'); return; }
        if (storedPwd !== passwordInput) { alert('पासवर्ड मेल नहीं खा रहा'); return; }

        setBasic(data.basic || {});
        setMeta(data.meta || {});
        const membersArr = data.members ? Object.entries(data.members).map(([id, value]) => ({ id, ...value })) : [];
        setMembers(membersArr);
        setCurrentRegId(regId);
        return;
      }

      // fallback: scan all registrations for aadhaar/memberId/phone
      const allSnap = await get(ref(db, 'registration'));
      if (!allSnap.exists()) { alert('❌ कोई रिकॉर्ड नहीं मिला'); return; }
      const regs = allSnap.val();
      let found = null;
      for (const [fid, rv] of Object.entries(regs)) {
        if (rv.basic && (rv.basic.mobile === regId || rv.basic.displayName === regId)) { found = { fid, rv }; break; }
        if (rv.members) {
          for (const [mk, mv] of Object.entries(rv.members)) {
            if ((mv.aadhaar && String(mv.aadhaar) === String(regId)) || (mv.memberId && String(mv.memberId) === String(regId))) { found = { fid, rv }; break; }
          }
          if (found) break;
        }
      }

      if (!found) { alert('❌ कोई रिकॉर्ड नहीं मिला'); return; }

      const storedPwd = found.rv.meta?.password;
      if (!storedPwd) { alert('पासवर्ड सेट नहीं है -- एडमिन से संपर्क करें'); return; }
      if (storedPwd !== passwordInput) { alert('पासवर्ड मेल नहीं खा रहा'); return; }

      setBasic(found.rv.basic || {});
      setMeta(found.rv.meta || {});
      const membersArr = found.rv.members ? Object.entries(found.rv.members).map(([id, value]) => ({ id, ...value })) : [];
      setMembers(membersArr);
      setCurrentRegId(found.fid);
    } catch (error) {
      console.error(error);
      alert("❌ डेटा लोड करने में समस्या आई");
    }
  };

  const handleMemberEdit = (member) => {
    setMemberEditData({ ...member });
    setShowMemberEdit(true);
  };

  const handleMemberUpdate = async (e) => {
    e.preventDefault();
    
    const { name, mobile, relation } = memberEditData;
    if (!name || !mobile || !relation) {
      alert("नाम, मोबाइल और संबंध आवश्यक है");
      return;
    }

    if (!validatePhone(mobile)) {
      alert("मोबाइल नंबर 10 अंकों का होना चाहिए");
      return;
    }

    if (memberEditData.aadhaar && !validateAadhaar(memberEditData.aadhaar)) {
      alert("आधार नंबर 12 अंकों का होना चाहिए");
      return;
    }

    try {
      const { id, ...updateData } = memberEditData;
      await update(ref(db, `registration/${currentRegId}/members/${id}`), updateData);
      alert('सदस्य अपडेट हुआ ✅');
      setShowMemberEdit(false);
      
      // Refresh member data
      const snapshot = await get(ref(db, `registration/${currentRegId}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const membersArr = data.members ? Object.entries(data.members).map(([id, value]) => ({ id, ...value })) : [];
        setMembers(membersArr);
      }
    } catch (error) {
      alert('अपडेट में त्रुटि: ' + error.message);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setMemberEditData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="container my-5">
      <div className="card shadow">
        <div className="card-header bg-primary text-white text-center fw-bold">
          🔍 परिवार खोजें (Unique Family ID)
        </div>

        <div className="card-body">
          {/* SEARCH FORM */}
          <form onSubmit={handleSearch} className="row g-3 justify-content-center">
            <div className="col-md-5">
              <label className="form-label fw-semibold">
                परिवार ID / Aadhaar / मोबाइल
              </label>
              <input
                className="form-control"
                placeholder="जैसे: 367543 या AADHAAR या मोबाइल"
                value={regId}
                onChange={(e) => setRegId(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">पासवर्ड</label>
              <input type="password" className="form-control" value={passwordInput} onChange={(e)=>setPasswordInput(e.target.value)} />
            </div>

            <div className="col-12 text-center">
              <button className="btn btn-success px-5">
                खोजें
              </button>
            </div>
          </form>

          {/* RESULT */}
          {basic && (
            <>
              <hr />

              {/* BASIC INFO */}
              <h5 className="text-success mb-3">🏠 परिवार की जानकारी</h5>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>मुखिया का नाम</th>
                    <td>{basic.headName}</td>
                    <th>परिवार प्रकार</th>
                    <td>{basic.familyType}</td>
                  </tr>
                  <tr>
                    <th>क्षेत्र</th>
                    <td>{basic.areaType}</td>
                    <th>घर का प्रकार</th>
                    <td>{basic.houseType}</td>
                  </tr>
                  <tr>
                    <th>राज्य</th>
                    <td>{basic.state}</td>
                    <th>शहर</th>
                    <td>{basic.city}</td>
                  </tr>
                  <tr>
                    <th>पता</th>
                    <td colSpan="3">{basic.address}</td>
                  </tr>
                </tbody>
              </table>

              {/* META INFO */}
              <h5 className="text-success mt-4 mb-3">📞 संपर्क विवरण</h5>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>मोबाइल नंबर</th>
                    <td><MaskedDisplay type="phone" value={meta?.contactNumber} /></td>
                    <th>रजिस्ट्रेशन तारीख</th>
                    <td>
                      {meta?.createdAt
                        ? new Date(meta.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* MEMBERS */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="text-success mt-4 mb-0">👨‍👩‍👧‍👦 परिवार के सदस्य</h5>
                <div>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => setShowAllMembers(true)}>सभी सदस्य देखें</button>
                </div>
              </div>

              {members.length === 0 ? (
                <div className="alert alert-warning">
                  कोई सदस्य नहीं जोड़ा गया
                </div>
              ) : (
                <table className="table table-striped table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>नाम</th>
                      <th>संबंध</th>
                      <th>लिंग</th>
                      <th>मोबाइल</th>
                      <th>कार्य स्थिति</th>
                      <th>क्रिया</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, index) => (
                      <tr key={m.id}>
                        <td>{index + 1}</td>
                        <td>{m.name}</td>
                        <td>{m.relation}</td>
                        <td>{m.gender}</td>
                        <td><MaskedDisplay type="phone" value={m.mobile} /></td>
                        <td>{m.workType || m.workingStatus || '-'}</td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-1" onClick={() => { setMemberViewData(m); setShowMemberView(true); }}>View</button>
                          {meta && meta.status === 'approved' ? (
                            <button className="btn btn-sm btn-secondary" disabled title="Approved - Only admin can edit">Edit</button>
                          ) : (
                            <button className="btn btn-sm btn-outline-success" onClick={() => handleMemberEdit(m)}>Edit</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Single Member View Modal */}
              {showMemberView && memberViewData && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header bg-info text-white">
                        <h5 className="modal-title">👤 सदस्य विवरण - {memberViewData.name}</h5>
                        <button className="btn-close" onClick={() => setShowMemberView(false)}></button>
                      </div>
                      <div className="modal-body">
                        {/* Personal Information Section */}
                        <div className="mb-4">
                          <h6 className="text-primary fw-bold mb-3">👤 व्यक्तिगत जानकारी</h6>
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td className="fw-semibold bg-light" style={{width: '25%'}}>सदस्य का नाम</td>
                                <td style={{width: '25%'}}>{memberViewData.name || '-'}</td>
                                <td className="fw-semibold bg-light" style={{width: '25%'}}>पिता / पति का नाम</td>
                                <td style={{width: '25%'}}>{memberViewData.fatherName || '-'}</td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">लिंग</td>
                                <td>{memberViewData.gender || '-'}</td>
                                <td className="fw-semibold bg-light">मुखिया से संबंध</td>
                                <td>{memberViewData.relation || '-'}</td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">जन्मदिनांक</td>
                                <td>{memberViewData.dob || '-'}</td>
                                <td className="fw-semibold bg-light">विवाहिक स्थिति</td>
                                <td>{memberViewData.maritalStatus || '-'}</td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">AADHAR No</td>
                                <td><MaskedDisplay type="aadhaar" value={memberViewData.aadhaar} /></td>
                                <td className="fw-semibold bg-light">ब्लड ग्रुप</td>
                                <td>{memberViewData.bloodGroup || '-'}</td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">Member ID</td>
                                <td>{memberViewData.memberId || '-'}</td>
                                <td className="fw-semibold bg-light">Unique ID</td>
                                <td>{memberViewData.id || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Contact Information Section */}
                        <div className="mb-4">
                          <h6 className="text-success fw-bold mb-3">📞 संपर्क जानकारी</h6>
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td className="fw-semibold bg-light" style={{width: '25%'}}>मोबाइल</td>
                                <td style={{width: '25%'}}><MaskedDisplay type="phone" value={memberViewData.mobile} /></td>
                                <td className="fw-semibold bg-light" style={{width: '25%'}}>WhatsApp No</td>
                                <td style={{width: '25%'}}><MaskedDisplay type="phone" value={memberViewData.whatsapp} /></td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">Email ID</td>
                                <td colSpan={3}>{memberViewData.email || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Address Information Section */}
                        <div className="mb-4">
                          <h6 className="text-warning fw-bold mb-3">🏠 पता जानकारी</h6>
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <td className="fw-semibold bg-light" style={{width: '25%'}}>वर्त्तमान पता</td>
                                <td style={{width: '75%'}} colSpan={3}>{memberViewData.currentAddress || '-'}</td>
                              </tr>
                              <tr>
                                <td className="fw-semibold bg-light">जन्म स्थान का पता</td>
                                <td colSpan={3}>{memberViewData.birthAddress || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Education Information Section */}
                        <div className="mb-4">
                          <h6 className="text-primary fw-bold mb-3">📚 शिक्षा की जानकारी</h6>
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th className="text-center">शिक्षा की स्थिति</th>
                                <th className="text-center">राज्य</th>
                                <th className="text-center">जिला</th>
                                <th className="text-center">कक्षा</th>
                                <th className="text-center">विषय</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-center">{memberViewData.educationStatus || '-'}</td>
                                <td className="text-center">{memberViewData.educationState || '-'}</td>
                                <td className="text-center">{memberViewData.educationDistrict || '-'}</td>
                                <td className="text-center">{memberViewData.educationClass || '-'}</td>
                                <td className="text-center">{memberViewData.educationSubject || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Work Information Section */}
                        <div className="mb-4">
                          <h6 className="text-info fw-bold mb-3">💼 कार्य की जानकारी</h6>
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th className="text-center">कार्य का प्रकार</th>
                                <th className="text-center">राज्य</th>
                                <th className="text-center">जिला</th>
                                <th className="text-center">क्षेत्र</th>
                                <th className="text-center">कार्य क्षेत्र का नाम</th>
                                <th className="text-center">विभाग/प्रकार</th>
                                <th className="text-center">पद/नाम</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-center">{memberViewData.workType || '-'}</td>
                                <td className="text-center">{memberViewData.workState || '-'}</td>
                                <td className="text-center">{memberViewData.workDistrict || '-'}</td>
                                <td className="text-center">{memberViewData.workArea || '-'}</td>
                                <td className="text-center">{memberViewData.workAreaName || '-'}</td>
                                <td className="text-center">{memberViewData.workDepartment || '-'}</td>
                                <td className="text-center">{memberViewData.workPosition || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setShowMemberView(false)}>Close</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All Members View Modal */}
              {showAllMembers && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-xl modal-dialog-centered" style={{maxWidth: '1100px'}}>
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">सभी सदस्य - तालिका दृश्य</h5>
                        <button className="btn-close" onClick={() => setShowAllMembers(false)}></button>
                      </div>
                      <div className="modal-body">
                        <div className="table-responsive">
                          <table className="table table-bordered table-striped">
                            <thead>
                              <tr>
                                {(() => {
                                  const keys = Array.from(new Set(members.flatMap(m => Object.keys(m))));
                                  return keys.map(k => <th key={k}>{k.replace(/([A-Z])/g, ' $1')}</th>);
                                })()}
                              </tr>
                            </thead>
                            <tbody>
                              {members.map((m, i) => (
                                <tr key={i}>
                                  {(() => {
                                    const keys = Array.from(new Set(members.flatMap(m => Object.keys(m))));
                                    return keys.map(k => <td key={k} style={{whiteSpace:'pre-wrap'}}>{typeof m[k] === 'object' ? JSON.stringify(m[k], null, 2) : (m[k] || '-')}</td>);
                                  })()}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setShowAllMembers(false)}>Close</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Member Edit Modal */}
              {showMemberEdit && memberEditData && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header bg-success text-white">
                        <h5 className="modal-title">✏️ सदस्य संपादित करें - {memberEditData.name}</h5>
                        <button className="btn-close" onClick={() => setShowMemberEdit(false)}></button>
                      </div>
                      <div className="modal-body">
                        <form onSubmit={handleMemberUpdate}>
                          {/* Personal Information Section */}
                          <div className="mb-4">
                            <h6 className="text-primary fw-bold mb-3">👤 व्यक्तिगत जानकारी</h6>
                            <table className="table table-bordered">
                              <tbody>
                                <tr>
                                  <td className="fw-semibold bg-light" style={{width: '25%'}}>सदस्य का नाम *</td>
                                  <td style={{width: '25%'}}>
                                    <input className="form-control" name="name" value={memberEditData.name || ''} onChange={handleEditChange} />
                                  </td>
                                  <td className="fw-semibold bg-light" style={{width: '25%'}}>पिता / पति का नाम</td>
                                  <td style={{width: '25%'}}>
                                    <input className="form-control" name="fatherName" value={memberEditData.fatherName || ''} onChange={handleEditChange} />
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">लिंग</td>
                                  <td>
                                    <select className="form-select" name="gender" value={memberEditData.gender || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option>पुरुष</option>
                                      <option>महिला</option>
                                      <option>अन्य</option>
                                    </select>
                                  </td>
                                  <td className="fw-semibold bg-light">मुखिया से संबंध *</td>
                                  <td>
                                    <select className="form-select" name="relation" value={memberEditData.relation || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option>मुखिया</option>
                                      <option>पत्नी</option>
                                      <option>पुत्र</option>
                                      <option>पुत्री</option>
                                    </select>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">जन्मदिनांक</td>
                                  <td>
                                    <input type="date" className="form-control" name="dob" value={memberEditData.dob || ''} onChange={handleEditChange} />
                                  </td>
                                  <td className="fw-semibold bg-light">विवाहिक स्थिति</td>
                                  <td>
                                    <select className="form-select" name="maritalStatus" value={memberEditData.maritalStatus || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option>अविवाहित</option>
                                      <option>विवाहित</option>
                                      <option>विधवा/विधुर</option>
                                    </select>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">AADHAR No</td>
                                  <td>
                                    <MaskedInput
                                      type="aadhaar"
                                      className="form-control"
                                      name="aadhaar"
                                      value={memberEditData.aadhaar || ''}
                                      onChange={handleEditChange}
                                    />
                                  </td>
                                  <td className="fw-semibold bg-light">ब्लड ग्रुप</td>
                                  <td>
                                    <select className="form-select" name="bloodGroup" value={memberEditData.bloodGroup || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option>A+</option>
                                      <option>A-</option>
                                      <option>B+</option>
                                      <option>B-</option>
                                      <option>AB+</option>
                                      <option>AB-</option>
                                      <option>O+</option>
                                      <option>O-</option>
                                    </select>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">Member ID</td>
                                  <td>
                                    <input className="form-control" value={memberEditData.memberId || ''} readOnly style={{backgroundColor: '#f8f9fa'}} />
                                  </td>
                                  <td className="fw-semibold bg-light">Unique ID</td>
                                  <td>
                                    <input className="form-control" value={memberEditData.id || ''} readOnly style={{backgroundColor: '#f8f9fa'}} />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Contact Information Section */}
                          <div className="mb-4">
                            <h6 className="text-success fw-bold mb-3">📞 संपर्क जानकारी</h6>
                            <table className="table table-bordered">
                              <tbody>
                                <tr>
                                  <td className="fw-semibold bg-light" style={{width: '25%'}}>मोबाइल *</td>
                                  <td style={{width: '25%'}}>
                                    <MaskedInput
                                      type="phone"
                                      className="form-control"
                                      name="mobile"
                                      value={memberEditData.mobile || ''}
                                      onChange={handleEditChange}
                                    />
                                  </td>
                                  <td className="fw-semibold bg-light" style={{width: '25%'}}>WhatsApp No</td>
                                  <td style={{width: '25%'}}>
                                    <MaskedInput
                                      type="phone"
                                      className="form-control"
                                      name="whatsapp"
                                      value={memberEditData.whatsapp || ''}
                                      onChange={handleEditChange}
                                    />
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">Email ID</td>
                                  <td colSpan={3}>
                                    <input type="email" className="form-control" name="email" value={memberEditData.email || ''} onChange={handleEditChange} />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Address Information Section */}
                          <div className="mb-4">
                            <h6 className="text-warning fw-bold mb-3">🏠 पता जानकारी</h6>
                            <table className="table table-bordered">
                              <tbody>
                                <tr>
                                  <td className="fw-semibold bg-light" style={{width: '25%'}}>वर्त्तमान पता</td>
                                  <td style={{width: '75%'}} colSpan={3}>
                                    <input className="form-control" name="currentAddress" value={memberEditData.currentAddress || ''} onChange={handleEditChange} />
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-semibold bg-light">जन्म स्थान का पता</td>
                                  <td colSpan={3}>
                                    <input className="form-control" name="birthAddress" value={memberEditData.birthAddress || ''} onChange={handleEditChange} />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Education Information Section */}
                          <div className="mb-4">
                            <h6 className="text-primary fw-bold mb-3">📚 शिक्षा की जानकारी</h6>
                            <table className="table table-bordered">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center">शिक्षा की स्थिति</th>
                                  <th className="text-center">राज्य</th>
                                  <th className="text-center">जिला</th>
                                  <th className="text-center">कक्षा</th>
                                  <th className="text-center">विषय</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    <select className="form-select" name="educationStatus" value={memberEditData.educationStatus || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option value="Running">Running</option>
                                      <option value="Complete">Complete</option>
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-select" name="educationState" value={memberEditData.educationState || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      {states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-select" name="educationDistrict" value={memberEditData.educationDistrict || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      {cities.filter(c => c.state === memberEditData.educationState).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <input className="form-control" name="educationClass" value={memberEditData.educationClass || ''} onChange={handleEditChange} placeholder="कक्षा" />
                                  </td>
                                  <td>
                                    <input className="form-control" name="educationSubject" value={memberEditData.educationSubject || ''} onChange={handleEditChange} placeholder="विषय" />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Work Information Section */}
                          <div className="mb-4">
                            <h6 className="text-info fw-bold mb-3">💼 कार्य की जानकारी</h6>
                            <table className="table table-bordered">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center">कार्य का प्रकार</th>
                                  <th className="text-center">राज्य</th>
                                  <th className="text-center">जिला</th>
                                  <th className="text-center">क्षेत्र</th>
                                  <th className="text-center">कार्य क्षेत्र का नाम</th>
                                  <th className="text-center">विभाग/प्रकार</th>
                                  <th className="text-center">पद/नाम</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    <select className="form-select" name="workType" value={memberEditData.workType || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      <option value="Govt Job">Govt Job</option>
                                      <option value="Private Job">Private Job</option>
                                      <option value="Business">Business</option>
                                      <option value="House Wife">House Wife</option>
                                      <option value="Other">Other</option>
                                      <option value="Na">Na</option>
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-select" name="workState" value={memberEditData.workState || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      {states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-select" name="workDistrict" value={memberEditData.workDistrict || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      {cities.filter(c => c.state === memberEditData.workState).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select className="form-select" name="workArea" value={memberEditData.workArea || ''} onChange={handleEditChange}>
                                      <option value="">चयन करें</option>
                                      {urbanAreas.filter(u => u.city === memberEditData.workDistrict).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <input className="form-control" name="workAreaName" value={memberEditData.workAreaName || ''} onChange={handleEditChange} placeholder="कार्य क्षेत्र का नाम" />
                                  </td>
                                  <td>
                                    <input className="form-control" name="workDepartment" value={memberEditData.workDepartment || ''} onChange={handleEditChange} placeholder="विभाग/प्रकार" />
                                  </td>
                                  <td>
                                    <input className="form-control" name="workPosition" value={memberEditData.workPosition || ''} onChange={handleEditChange} placeholder="पद/नाम" />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className="text-center mt-4">
                            <button type="button" className="btn btn-secondary me-3" onClick={() => setShowMemberEdit(false)}>Cancel</button>
                            <button type="submit" className="btn btn-success btn-lg px-5">💾 अपडेट करें</button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
